import React, { useMemo } from 'react';

import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { Apollo } from 'src/gql/generated';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';
import { PriorityDisplay } from '../cells';

const PRIORITIES = [
  { id: Apollo.WorkflowPriority.Urgent, label: 'Urgent' },
  { id: Apollo.WorkflowPriority.High, label: 'High' },
  { id: Apollo.WorkflowPriority.Medium, label: 'Medium' },
  { id: Apollo.WorkflowPriority.Low, label: 'Low' },
];

export function PriorityFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map(String) : []);
  }, [filterModel, field]);

  const handleToggle = (priorityId: string) => {
    const currentValues = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(priorityId);

    const newValues = isSelected
      ? currentValues.filter((v: string) => String(v) !== priorityId)
      : [...currentValues, priorityId];

    const updatedItems = filterModel.items.filter(item => item.field !== field);

    if (newValues.length > 0) {
      updatedItems.push({ field, operator, value: newValues });
    }

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      options={PRIORITIES}
      renderOption={option => <PriorityDisplay priority={option.id} />}
      selectedIds={selectedIds}
      onToggle={handleToggle}
    />
  );
}
